var _ = require('lodash'),
  BinObject = require('../models/binObject'),
  cache = require('../models/cache'),
  configManager = require('../config/configManager'),
  CONST_EVENTS = require('../constants/events'),
  cssPrefix = configManager.getSystemConfig().elementPrefix,
  events = require('../events/eventObserver'),
  endpointMonitor = require('../libraries/endpointMonitor'),
  htmlUtility = require('./htmlUtility'),
  log = require('../libraries/logger').getLoggerInstance('Base.DFUtility'),
  RSVP = require('rsvp'),
  UUID = require('uuid');

var publicMethods = {
  /**
   * Empty data object used to store a cache of bins we have checked for profiling 
   */
  binCache: {},
  /**
   * A few utilites to help make using the bin cache easier 
   */
  binCacheUtility: {
    /**
     * Create a record in the in memory cache for bin profiling
     * @param {string} bin The bin value or full card number being processed
     * @param {ProfilingObject=} profilingObject An optional flag to indicate this bin is configured to require profiling
     * @returns {BinObject} The newly created bin object that was stored in cache
     */
    create: function (bin, cardType, profilingObject) {
      var binObject = new BinObject(bin, cardType, profilingObject);
      publicMethods.binCache[bin] = binObject;
      return binObject;
    },
    /**
     * Simple helper to update the in memory cache of the bin configuration for a given bin value. This function does a merge on the objects in memory removing
     * the need to pass in the entire object to update field values
     * @param {BinObject} binData The data object to store in cache
     */
    update: function (binData) {
      if (binData !== undefined && binData.bin !== undefined && binData.bin !== "") {
        var binEntry = publicMethods.binCacheUtility.find(binData.bin);
        if (binEntry === undefined) {
          binEntry = publicMethods.binCache[binData.bin] = {};
        }
        _.merge(binEntry, binData);
      }
    },
    /**
     * Get the bin profiling delay from the in memory bin cache. If no delay was found, we will automatically use the system default value
     * @param {String} bin The bin to find the profiling delay for
     * @returns {number} The stored bin delay or the system default bin delay if none was found for the given bin
     */
    getProfilingDelay: function (bin) {
      var binSettings = publicMethods.binCacheUtility.find(bin);
      if (binSettings !== undefined && 'profilingDelay' in binSettings && !_.isNaN(binSettings.profilingDelay)) {
        return binSettings.profilingDelay;
      } else {
        return configManager.getSystemConfig().defaultBinProfilingDelay;
      }
    },
    /**
     * Get a given bin value from in memory cache. Bin object returned is the response from the BinEnabled call
     * @param {string} bin The partial or full account number to find in cache
     * @returns {BinObject} The bin configuration object or undefined if no bin was found
     */
    find: function (bin) {
      if (bin !== undefined) {
        return _.find(publicMethods.binCache, function (entry, key) {
          if (key === bin) {
            return true
          } else if (bin.length > key.length && _.startsWith(bin, key)) {
            return true
          } else {
            return false
          }
        });
      }
    }
  },
  /**
   * Creates a hidden iframe and injects it into the page. It will check for an iframe with the same Id and remove it if found. This allows it to be run repeatedly without being concerned about Id collision.
   * @param {string} dfUrl - The URL to use as the iframe 'src', this should be the DFUrl with query params on a standard DF call and empty during a bin POST.
   * @returns iframe - The iframe that was created and injected into the page
   */
  createCollectionFrame: function (iframeSrc) {
    return new RSVP.Promise(function (resolve, reject) {
      var iframe;
      try {
        var iframeId = cssPrefix + 'collector',
          oldIframe = document.getElementById(iframeId),
          iframeStyles;

        // INSIGHT - To keep the DOM clean, make sure we haven't already run this call once. With the addition of Bin based detection its possible this call could be triggered multiple times per page load.
        if (oldIframe !== null) {
          document.body.removeChild(oldIframe);
        }

        // We have a url - build iframe to inject into page
        iframe = htmlUtility.factories.iframe(iframeId);
        iframe.onload = function () {
          // this would fire for the first load but won't get data until the first post
          // since the dfUrl is not in the browser yet
          endpointMonitor.captureTiming(iframeSrc, true);
        }

        // Hide iframe and push off of page view
        // INSIGHT - According to MDN docs Opera .style.PROP is read only - so we must
        // modify the values indirectly: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/style
        iframeStyles = iframe.style;
        iframeStyles.display = 'none';
        iframeStyles.position = 'absolute';
        iframeStyles.top = '-1000px';
        iframeStyles.left = '-1000px';

        resolve(iframe);

      } catch (error) {
        log.error('Failed while attempting to create DF window: ' + error);
        reject();
      }

      return;
    })

  },
  /**
   * Inject an iframe with a form into the page and post the Bin value over to DF
   * @param {string} formAction - The URL that should be used to post the bin value to
   * @param {node} iframe - The DOM element of the iframe the form should be injected into
   * @param {BinObject} binData - The bin object referencing the bin we're currently profiling
   */
  postBinCollectionFrame: function (formAction, iframe, binData) {
    return new RSVP.Promise(function (resolve, reject) {
      try {
        var form = htmlUtility.factories.form(formAction, 'POST');

        if (binData !== undefined) {
          document.body.appendChild(iframe);

          binData.nonce = UUID.v4();
          form.appendChild(htmlUtility.factories.input('nonce', binData.nonce, 'hidden'));

          if (binData.bin !== undefined && binData.bin !== "") {
            form.appendChild(htmlUtility.factories.input('bin', binData.bin, 'hidden'));
          }

          if (binData.cardType !== undefined && binData.cardType !== "") {
            form.appendChild(htmlUtility.factories.input('cardType', binData.cardType, 'hidden'));
          }

          if (htmlUtility.injectFormIntoIframe(form, iframe)) {
            binData.profiling.startTime = new Date();
            publicMethods.binCacheUtility.update(binData);

            form.submit();

            return resolve();
          } else {
            log.error("Failed to inject form into iframe");
          }
        } else {
          log.error('Invalid bin value [' + binData + '], unable to render collect form correctly.');
        }
      } catch (error) {
        log.error('Failed to post bin collection frame:' + error);
        reject(error);
      }

      reject();
    });
  },
  /**
   * Looks for the bin in the different places it can be stored and returns the found value. If an order object is passed in the order object is assumed to be the most recent data source
   * @param {Object=} orderObject - the Order object to use as a source
   * @returns {{bin: string, cardType: string}} bin - the bin value that was found, this value can be undefined if no bin was passed
   */
  getBinOrderObjectDetails: function (orderObject) {
    var bin, cardType;

    try {
      // If an order object is passed in assume the order object is the most recent data source
      if (orderObject !== undefined) {
        var binFromOrder = publicMethods.getFieldFromAccountObject(orderObject, 'AccountNumber');

        if (binFromOrder !== undefined) {
          bin = binFromOrder;
          cardType = publicMethods.getFieldFromAccountObject(orderObject, 'CardType');
        } else {
          bin = cache.bin;
        }
      } else {
        // otherwise we assume the cache is the most recent source
        if (cache.bin !== undefined) {
          bin = cache.bin;
        } else {
          bin = publicMethods.getFieldFromAccountObject(cache.orderObject, 'AccountNumber');
          cardType = publicMethods.getFieldFromAccountObject(cache.orderObject, 'CardType');
        }

        // Last ditch effort is to look for the bin in the JWT order object
        if (bin === undefined && cache.jwtOrderObject !== undefined) {
          bin = publicMethods.getFieldFromAccountObject(cache.jwtOrderObject, 'AccountNumber');
          cardType = publicMethods.getFieldFromAccountObject(cache.jwtOrderObject, 'CardType');
        }
      }

    } catch (error) {
      log.error('Failed while finding bin: ' + error);
    }

    return {
      bin: bin,
      cardType: cardType
    };
  },
  /**
   * Helper to safely get a field from the Account object without causing ReferenceErrors
   * @param {Object} orderObject The full order object to try and extract the field from
   * @param {String} fieldToFind The field that we should look for within the Account object
   * @returns {String} The field value found or undefined if no field was found
   */
  getFieldFromAccountObject: function (orderObject, fieldToFind) {
    var fieldValue;

    if (orderObject != undefined) { // eslint-disable-line
      if ('Consumer' in orderObject && orderObject.Consumer !== undefined) {
        if ('Account' in orderObject.Consumer && orderObject.Consumer.Account !== undefined) {
          if (fieldToFind in orderObject.Consumer.Account) {
            fieldValue = orderObject.Consumer.Account[fieldToFind];
          }
        }
      }
    }

    return fieldValue;
  },
  /**
   * Small utility to convert an object of Key value pairs into a string of query parameters for a URL.
   * The string is defaulted to '?' and each kVP is appended and url encoded. The result should be added directly to the end of
   * a URL w/o any additional changes needed
   * @param {Object} queryParams An object of key value string pairs
   * @returns {String} The query parameter string thats should be appended to a URL. The string includes the starting '?'
   */
  constructQueryParameterString: function (queryParams) {
    var queryString = '?';
    if (queryParams && typeof queryParams === 'object') {
      _.each(queryParams, function (value, key) {
        queryString += encodeURIComponent(key) + '=' + encodeURIComponent(value) + '&';
      })
      queryString += 'origin=Songbird';
    }
    return queryString;
  },
  /**
   * A helper that will hold a promise until either the bin.profilingCompleted event is triggered or 
   * deviceProfilingTimeout length of time has elapsed.
   * @param {BinObject} binData The bin data object representing the bin profiling session we're waiting for to complete
   * @returns {Promise} A promise that resolves when the event is completed successfully, or rejected if profiling is reported as 
   * a failure or the deviceProfileTimeout has elapsed
   */
  waitForProfilingToComplete: function (binData) {
    return new RSVP.Promise(function (resolve, reject) {
      try {
        var profilingCompleted = false,
          profileStartTime = new Date(),
          qosTimer;

        qosTimer = setTimeout(function () {
          log.error("Bin profiling exceeded QOS setting [" + binData.profiling.delay + "]ms")
          profilingCompleted = true;
          reject('Profiling timed out');
        }, binData.profiling.delay);

        events.subscribe(CONST_EVENTS.INTERNAL.DF_BIN_PROFILING_COMPLETED, function (completedId, completedStatus) {
          if (completedId === binData.nonce) {
            if (!profilingCompleted) {
              log.debug("Browser device profiling completed in [" + (new Date().getTime() - profileStartTime.getTime()) + "]ms")
              clearTimeout(qosTimer);

              if (completedStatus) {
                if (binData !== undefined) {
                  binData.profiling.completed = true;
                  publicMethods.binCacheUtility.update(binData);
                }
                resolve();
              } else {
                reject({
                  Type: 'DF Status Flag'
                });
              }
            } else {
              log.error('DF completed after the allotted wait time. Total time [' + (new Date().getTime() - profileStartTime.getTime()) + ']');
              resolve();
            }
          }
        });
      } catch (error) {
        reject(error);
      }
    })
  }
};

module.exports = publicMethods;


//////////////////
// WEBPACK FOOTER
// ./utilities/deviceFingerprinterUtility.js
// module id = 62
// module chunks = 5